<template>
  <div>
    <template v-if="label">
      <FieldLabel>{{ label }}</FieldLabel>
    </template>
    <div
      :ref="`input-wrapper-${fieldName}`"
      class="input-wrapper d-flex w-100 position-relative"
      :class="{ focus: focus }"
    >
      <input
        v-model="inputValue"
        class="input border-0 w-100 position-relative px-1"
        :disabled="disabled"
        :placeholder="placeholder"
        @keyup="keyupHandler"
        @keydown="keydownHandler"
        @focus="focus = true"
        @blur="focus = false"
      >
    </div>
    <SelectionList
      v-if="suggestionList"
      :field-name="`${fieldName}-suggestion-list`"
      :width="width()"
      :open="suggestionListOpen"
      :item-list="suggestionItems"
      :select-item="selectItem"
      :highlighted-index-changed="highlightedIndexChanged"
    />
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue'
import { KEY_CODES } from '../../general/helper'
import { IRecommendation } from '../interfaces'
import FieldLabel from './FieldLabel.vue'
import SelectionList from './SelectionList.vue'

export default defineComponent({
  components: { FieldLabel, SelectionList },

  props: {
    label: { type: String, default: null },
    placeholder: { type: String, default: '' },
    fieldName: { type: String, default: '' },
    fieldValue: { type: String as PropType<string | null>, default: null },
    valueChanged: { type: Function, default: () => console.log('valueChanged not implemented') }, // eslint-disable-line no-console
    suggestionList: { type: Array<IRecommendation>, default: null },
    suggestionMinChars: { type: Number, default: 3 },
    enter: { type: Function, default: () => console.log('enter not implemented') }, // eslint-disable-line no-console
    disabled: { type: Boolean, default: false },
  },

  data: () => {
    return {
      focus: false,
      suggestionListOpen: false,
      highlightedIndex: -1,
    }
  },

  computed: {
    inputValue: {
      get() {
        return this.fieldValue
      },
      set(value: string) {
        this.valueChanged(this.fieldName, value)
      },
    },
    suggestionItems() {
      return this.suggestionList.filter( item =>
        this.inputValue ? item.listDisplay.toLowerCase().startsWith(this.inputValue.toLowerCase()) : false
      )
    },
  },

  methods: {
    width() {
      const wrapper = this.$refs[`input-wrapper-${this.fieldName}`] as HTMLElement

      return !!wrapper ? wrapper.getClientRects()[0].width : 150
    },
    keyupHandler(event: KeyboardEvent) {
      switch (event.code) {
        case KEY_CODES.ARROW_DOWN:
          this.suggestionListOpen = true
          break
        case KEY_CODES.ESCAPE:
          this.suggestionListOpen = false
          break
        case KEY_CODES.ARROW_UP:
          break
        case KEY_CODES.ENTER:
          this.enter(this.inputValue)
          break
        default:
          if (this.inputValue && this.inputValue.length >= this.suggestionMinChars) {
            this.suggestionListOpen = true
          } else {
            this.suggestionListOpen = false
          }
          break
      }
    },
    keydownHandler(event: KeyboardEvent) {
      switch (event.code) {
        case KEY_CODES.ENTER:
        case KEY_CODES.TAB:
          if (
            this.suggestionListOpen &&
            this.suggestionItems.length &&
            this.highlightedIndex < 0
          ) {
            this.selectItem(this.suggestionItems[0])
          }
          break
      }
    },
    selectItem(item: IRecommendation) {
      if (!item) {
        return
      }
      this.inputValue = item.listDisplay
      this.suggestionListOpen = false
    },
    highlightedIndexChanged(index: number) {
      this.highlightedIndex = index
    },
  },
})
</script>

<style lang="scss" scoped>
@import '../../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

.input-wrapper {
  height: 32px;
  border: 2px solid $color-expinet-gray;

  &.focus {
    border: 2px solid $color-expinet-blue;
  }
}

.input {
  color: $color-aida-black;
  box-shadow: none;

  &:focus {
    outline: 0;
  }

  &:disabled {
    background-color: $color-expinet-gray;
    cursor: not-allowed;
  }
}
</style>
